import { useCallback, useEffect, useRef, useState } from 'react'
import { router } from '@inertiajs/react'
import type { ManagementHistoryKind, ManagementHistoryPage } from '@/frontend/features/Fde/projectManagementHistoryTypes'

export default function useProjectManagementHistory(projectId: string, revisionKey: string, open: boolean, kind: ManagementHistoryKind) {
  const [snapshot, setSnapshot] = useState<ManagementHistoryPage | null>(null)
  const [busy, setBusy] = useState(false)
  const [failed, setFailed] = useState(false)
  const request = useRef(0)
  const load = useCallback((before?: string | null) => {
    const sequence = ++request.current
    setBusy(true)
    setFailed(false)
    if (!before) setSnapshot(null)
    router.reload({ only: ['project_management_history'], data: { history_before: before || '', history_kind: kind },
      onSuccess: (page) => {
        if (sequence !== request.current) return
        const next = page.props.project_management_history as ManagementHistoryPage | undefined
        if (next?.project_id !== projectId || next.kind !== kind) {
          setSnapshot(null); setFailed(true); return
        }
        // Older pages extend one scoped list; retries cannot duplicate entries.
        setSnapshot(previous => before && previous ? { ...next,
          entries: [...previous.entries, ...next.entries.filter(entry => !previous.entries.some(item => item.id === entry.id))] } : next)
      },
      onError: () => { if (sequence === request.current) { if (!before) setSnapshot(null); setFailed(true) } },
      onFinish: () => { if (sequence === request.current) setBusy(false) },
    })
  }, [projectId, kind])
  useEffect(() => {
    // Opening, changing filters/revisions and reconnect reloads converge to Rails.
    // Cleanup rejects in-flight callbacks after close or a project/org switch.
    if (open) load()
    else { setSnapshot(null); setBusy(false); setFailed(false) }
    return () => { request.current++ }
  }, [open, revisionKey, load])
  const scoped = snapshot?.project_id === projectId && snapshot.kind === kind ? snapshot : null
  return { snapshot: scoped, busy, failed, load }
}
